// Gets the GPU ready for what the scene can show before the camera turns to
// it. three.js compiles a material's shader and uploads its textures on the
// object's first draw, so a camera move that brings bodies into view for the
// first time stalls on them: flying from the Moon to the ISS swung Saturn,
// Titan and Enceladus into view at once, a 190 ms frame at full speed (a
// 4096 x 2048 map is 50 to 80 ms to upload on its own).
//
// This does that work ahead, a piece at a time, only while nobody is touching
// the page and the camera is not being flown: one texture, or a few shaders
// (compiled in parallel where KHR_parallel_shader_compile is there), per
// idle slot, 200 ms apart at least. Only what is visible is warmed (hidden
// modes keep their memory until used), and the scene is scanned again every
// few seconds for bodies and maps that arrived since. An upload is still one
// long frame, so the biggest maps are left to their first draw as before: an
// 8192 x 4096 one took 400 ms, a pause anyone would see even with the page
// still; up to 5760 x 2880 is under 90 ms. Desktop-class devices only: on
// phones the extra GPU memory for maps never looked at costs more than the
// stall it saves.
//
// startGpuWarmup({ renderer, scene, camera, isBusy }) once the viewer runs;
// window.__gpuWarm has the counts. compileShownNow() does the shaders of
// everything shown at once, before the first frame.
//
// three.js keys a program by where it draws: to the screen it is tone mapped
// with sRGB output, into a render target it is linear. The viewer draws its
// scene into the composer's targets, so compiles here have a target bound.
// Until 2 Nov 2026 they had none, and every shader warmed was a copy the
// viewer never drew (28 of the 56 programs on the desktop viewer), compiled
// again on its first draw.
import {
  WebGLRenderTarget, Mesh, BoxGeometry, MeshBasicMaterial, OrthographicCamera, Color,
  BackSide, HalfFloatType, LinearFilter, RGBAFormat, LinearSRGBColorSpace, CubeUVReflectionMapping,
} from 'three';

const QUIET_MS = 2000;
const STEP_GAP_MS = 200;
const RESCAN_MS = 8000;
const PROGRAMS_PER_STEP = 4;
const MAX_WARM_PIXELS = 5760 * 2880;

let compileTarget = null;

// renderer.compile with the scene's kind of target bound: the materials it
// prepared, or null if it threw. Anything compiled ahead for the viewer's
// scene goes through this (compileForSceneAsync to wait for it).
export function compileFor(renderer, object, camera, scene) {
  if (!compileTarget) compileTarget = new WebGLRenderTarget(1, 1);
  const previous = renderer.getRenderTarget();
  renderer.setRenderTarget(compileTarget);
  try {
    return renderer.compile(object, camera, scene);
  } catch (_) {
    return null;
  } finally {
    renderer.setRenderTarget(previous);
  }
}

// Resolves once every material's program can be used without waiting on the
// driver, or after limitMs. A material with no program is let go: the
// renderer's own compileAsync throws on one (see compileOne).
function programsReady(renderer, materials, limitMs) {
  const props = renderer.properties;
  const started = performance.now();
  return new Promise((resolve) => {
    const check = () => {
      for (const m of materials) {
        const program = props.get(m).currentProgram;
        let ready = true;
        try { ready = !program || program.isReady() !== false; } catch (_) { ready = true; }
        if (ready) materials.delete(m);
      }
      if (!materials.size || performance.now() - started > limitMs) resolve();
      else setTimeout(check, 10);
    };
    check();
  });
}

export function compileForSceneAsync(renderer, object, camera, scene, limitMs = 5000) {
  const materials = compileFor(renderer, object, camera, scene);
  return materials ? programsReady(renderer, materials, limitMs) : Promise.resolve();
}

// Where the driver compiles slowly a program asked for before it is ready
// holds the page until it is: Chrome and Edge on Windows translate WebGL to
// Direct3D and compile it with FXC, a second or more for a big shader on a
// first visit with no shader cache, and the GPU process waits with the page,
// so the whole browser stalls. This is that wait's limit for a model that
// is not shown until its programs are ready: long, since showing it sooner
// only moves the wait into a frame.
export const SLOW_COMPILE_LIMIT_MS = 60000;

// True once no program the renderer has made is still compiling.
function everyProgramReady(renderer) {
  for (const program of (renderer.info && renderer.info.programs) || []) {
    try { if (program.isReady() === false) return false; } catch (_) { /* a released program */ }
  }
  return true;
}

// The post chain's programs, compiled without drawing anything: the composer
// runs once with renderer.render standing in for renderer.compile, so each
// pass compiles what it would draw, into the target it would draw into (the
// last one to the screen), with the defines it sets as it renders (the
// output pass sets its tone mapping there). Passes switched off for now are
// run too. The scene itself is left out (skip), as compileShownNow does it.
// Left to the first frame these were twenty programs compiled one after
// another inside it.
export function compileComposer(renderer, composer, skip) {
  const materials = new Set();
  if (!composer || !Array.isArray(composer.passes)) return materials;
  const render = renderer.render;
  const enabled = composer.passes.map((pass) => pass.enabled);
  renderer.render = (object, camera) => {
    if (object === skip) return;
    try {
      const got = renderer.compile(object, camera);
      if (got) for (const m of got) materials.add(m);
    } catch (_) { /* left to its first frame */ }
  };
  try {
    for (const pass of composer.passes) pass.enabled = true;
    composer.render(0);
  } catch (_) {
    // A pass that cannot run before the first frame compiles in it.
  } finally {
    renderer.render = render;
    composer.passes.forEach((pass, i) => { pass.enabled = enabled[i]; });
  }
  return materials;
}

// A black stand-in for the env map PMREMGenerator.fromScene makes (at its
// fixed 256 size, a 768 x 1024 cube-UV target): three keys a material's
// program by its env map's mapping and height, not its contents, so the
// scene's metallic materials compile once against this, and the real map
// put in its place later compiles nothing. Black reflects what no env map
// does.
export function pmremStandIn(renderer, cubeSize = 256) {
  const target = new WebGLRenderTarget(3 * Math.max(cubeSize, 16 * 7), 4 * cubeSize, {
    magFilter: LinearFilter,
    minFilter: LinearFilter,
    generateMipmaps: false,
    type: HalfFloatType,
    format: RGBAFormat,
    colorSpace: LinearSRGBColorSpace,
    depthBuffer: false,
  });
  target.texture.mapping = CubeUVReflectionMapping;
  target.texture.name = 'PMREM stand-in';
  const previous = renderer.getRenderTarget();
  const clear = renderer.getClearColor(new Color());
  const alpha = renderer.getClearAlpha();
  renderer.setRenderTarget(target);
  renderer.setClearColor(0x000000, 0);
  renderer.clear(true, false, false);
  renderer.setRenderTarget(previous);
  renderer.setClearColor(clear, alpha);
  return target;
}

// PMREMGenerator.fromScene compiles its programs in the call that draws with
// them, so the page waited on each: the sky's took 4 s at load on a slow
// compiler, queued behind the scene's own. Here the scene, the box that
// clears each cube face and the blur are compiled first and waited for, then
// it runs. The blur comes from sizing the generator as fromScene does (three
// r160's internals); without them fromScene compiles it as before.
export async function pmremFromSceneAsync(renderer, pmrem, envScene, sigma, near, far) {
  const camera = new OrthographicCamera();
  const materials = new Set();
  const add = (got) => { if (got) for (const m of got) materials.add(m); };
  add(compileFor(renderer, envScene, camera, envScene));
  const box = new Mesh(new BoxGeometry(), new MeshBasicMaterial({ side: BackSide }));
  add(compileFor(renderer, box, camera, null));
  if (typeof pmrem._setSize === 'function' && typeof pmrem._allocateTargets === 'function') {
    pmrem._setSize(256);
    pmrem._allocateTargets().dispose();
    const planes = pmrem._lodPlanes;
    if (pmrem._blurMaterial && planes && planes[0]) add(compileFor(renderer, new Mesh(planes[0], pmrem._blurMaterial), camera, null));
  }
  await programsReady(renderer, materials, SLOW_COMPILE_LIMIT_MS);
  try {
    return pmrem.fromScene(envScene, sigma, near, far);
  } finally {
    box.geometry.dispose();
    box.material.dispose();
  }
}

function textureReady(tex) {
  if (!tex || !tex.isTexture || tex.isRenderTargetTexture || tex.isVideoTexture) return false;
  if (!(tex.version > 0)) return false;
  const img = tex.image;
  if (img === null || img === undefined) return false;
  if (Array.isArray(img)) return img.length > 0 && img.every((i) => i && i.complete !== false);
  return img.complete !== false;
}

function eachTexture(material, fn) {
  for (const key in material) {
    const v = material[key];
    if (v && v.isTexture) fn(v);
  }
  const uniforms = material.uniforms;
  if (!uniforms) return;
  for (const key in uniforms) {
    const v = uniforms[key] && uniforms[key].value;
    if (!v) continue;
    if (v.isTexture) fn(v);
    else if (Array.isArray(v)) for (const t of v) if (t && t.isTexture) fn(t);
  }
}

// Drawn when the camera looks its way: visible all the way up to the scene.
// A model hidden only while it is out of view (the ISS) carries
// userData.gpuWarm and is warmed as if shown.
function shown(object, scene) {
  for (let o = object; o; o = o.parent) {
    if (!o.visible && !(o.userData && o.userData.gpuWarm)) return false;
    if (o === scene) return true;
  }
  return false;
}

export function startGpuWarmup({ renderer, scene, camera, isBusy = () => false }) {
  const props = renderer.properties;
  if (!props || typeof renderer.initTexture !== 'function') return null;
  const stats = { programs: 0, textures: 0, pendingPrograms: 0, pendingTextures: 0, scans: 0, skipped: 0 };
  const textures = [];
  const meshes = [];
  const queuedTex = new WeakSet();
  const queuedMat = new WeakSet();
  let lastInput = performance.now();
  let lastStep = 0;
  let lastScan = -Infinity;
  // A compile is in flight; the next step waits for it.
  let compiling = false;

  const note = () => { lastInput = performance.now(); };
  for (const ev of ['pointerdown', 'pointermove', 'wheel', 'keydown', 'touchstart']) {
    window.addEventListener(ev, (e) => {
      if (ev === 'pointermove' && !e.buttons) return;
      note();
    }, { passive: true, capture: true });
  }

  const uploaded = (tex) => props.get(tex).__version === tex.version;

  // renderer.compileAsync's own wait reads currentProgram.isReady() on a
  // timer, and throws (uncaught, in that timer) when a material lets go of its
  // program meanwhile, as the scale switch's rebuilt orbit lines do. Compile,
  // then wait here instead: a material with no program, or one still not
  // ready after a few seconds, is let go.
  function compileOne(mesh) {
    const materials = compileFor(renderer, mesh, camera, scene);
    if (!materials) {
      stats.skipped++;
      return null;
    }
    return programsReady(renderer, materials, 5000);
  }
  const compiled = (material) => Boolean(props.get(material).currentProgram);

  function scan() {
    lastScan = performance.now();
    stats.scans++;
    scene.traverse((object) => {
      const material = object.material;
      if (!material || !(object.isMesh || object.isPoints || object.isLine || object.isSprite)) return;
      if (!shown(object, scene)) return;
      const materials = Array.isArray(material) ? material : [material];
      let needsProgram = false;
      for (const m of materials) {
        if (!m) continue;
        if (!compiled(m) && !queuedMat.has(m)) { queuedMat.add(m); needsProgram = true; }
        eachTexture(m, (tex) => {
          if (queuedTex.has(tex) || !textureReady(tex) || uploaded(tex) || area(tex) > MAX_WARM_PIXELS) return;
          queuedTex.add(tex);
          textures.push(tex);
        });
      }
      if (needsProgram) meshes.push(object);
    });
    // Smaller maps first: more of the scene is ready sooner, and the long
    // uploads come last, when the page has been quiet longest.
    textures.sort((a, b) => area(a) - area(b));
    stats.pendingPrograms = meshes.length;
    stats.pendingTextures = textures.length;
  }

  function area(tex) {
    const img = Array.isArray(tex.image) ? tex.image[0] : tex.image;
    return (img && (img.width || img.videoWidth) || 1) * (img && (img.height || img.videoHeight) || 1) * (Array.isArray(tex.image) ? tex.image.length : 1);
  }

  function step() {
    const now = performance.now();
    if (document.hidden || compiling || isBusy() || now - lastInput < QUIET_MS || now - lastStep < STEP_GAP_MS) return;
    if (!meshes.length && !textures.length) {
      if (now - lastScan > RESCAN_MS) scan();
      return;
    }
    lastStep = now;
    if (meshes.length) {
      const batch = meshes.splice(0, PROGRAMS_PER_STEP).filter((m) => m.parent && shown(m, scene));
      stats.pendingPrograms = meshes.length;
      if (!batch.length) return;
      compiling = true;
      Promise.all(batch.map(compileOne))
        .then(() => { stats.programs += batch.length; })
        .finally(() => { compiling = false; });
      return;
    }
    const tex = textures.shift();
    stats.pendingTextures = textures.length;
    // Still wanted and still not on the GPU (the frame may have drawn it).
    if (!textureReady(tex) || uploaded(tex)) return;
    try {
      renderer.initTexture(tex);
      stats.textures++;
    } catch (_) {
      stats.skipped++;
    }
  }

  const idle = window.requestIdleCallback
    ? (fn) => window.requestIdleCallback(fn, { timeout: 1000 })
    : (fn) => setTimeout(fn, 250);
  const loop = () => {
    try { step(); } catch (_) { /* never let the warm-up break the page */ }
    setTimeout(() => idle(loop), STEP_GAP_MS);
  };
  idle(loop);

  if (typeof window !== 'undefined') window.__gpuWarm = stats;
  return stats;
}

// Every shown material's shader and the post chain's, compiled before the
// first frame draws them. Left to the first frame, each one held the main
// thread while the driver compiled it, one after another: 1.3 s at load on a
// mid-range phone with no shader cache, and a tap on the loading screen
// waited for all of it. Here they are all started at once and compile in
// parallel where the browser has KHR_parallel_shader_compile, with the main
// thread free between batches.
//
// Resolves once every program the renderer has made is ready (these, and
// any compiled ahead elsewhere meanwhile), or after limitMs. Until 3 Nov 2026
// the limit was 2.5 s: where shaders compile slowly (Edge on Windows on a
// first visit, see SLOW_COMPILE_LIMIT_MS) the loading screen went with most
// of them still compiling, and the first frames waited on each in turn, the
// browser stalled for seconds. onSlow(ms) is called once if the wait passes
// slowAfterMs, for the loading screen to say why.
export async function compileShownNow({ renderer, scene, camera, composer = null, limitMs = 40000, slowAfterMs = 3000, onSlow = null }) {
  if (!renderer || typeof renderer.compile !== 'function') return;
  const props = renderer.properties;
  const started = performance.now();
  const seen = new Set();
  const meshes = [];
  scene.traverse((object) => {
    const material = object.material;
    if (!material || !(object.isMesh || object.isPoints || object.isLine || object.isSprite)) return;
    if (!shown(object, scene)) return;
    let fresh = false;
    for (const m of Array.isArray(material) ? material : [material]) {
      if (m && !seen.has(m) && !props.get(m).currentProgram) { seen.add(m); fresh = true; }
    }
    if (fresh) meshes.push(object);
  });
  for (let i = 0; i < meshes.length; i += 8) {
    for (const mesh of meshes.slice(i, i + 8)) compileFor(renderer, mesh, camera, scene);
    await new Promise((resolve) => setTimeout(resolve, 0));
  }
  if (composer) compileComposer(renderer, composer, scene);
  let toldSlow = false;
  await new Promise((resolve) => {
    const check = () => {
      const waited = performance.now() - started;
      if (everyProgramReady(renderer) || waited > limitMs) { resolve(); return; }
      if (!toldSlow && onSlow && waited > slowAfterMs) {
        toldSlow = true;
        try { onSlow(waited); } catch (_) { /* the wait goes on */ }
      }
      setTimeout(check, 16);
    };
    check();
  });
}
